// React and related imports
import React from 'react';

// Flowbite UI components and icons imports
import { Sidebar as FbSidebar } from 'flowbite-react';
import {
  HiChartPie,
  HiOutlineCollection,
  HiOutlineDocumentAdd,
  HiUser,
  HiSpeakerphone,
  HiAcademicCap,
} from 'react-icons/hi';
import { BiSupport } from 'react-icons/bi';
import { HiScale } from 'react-icons/hi2';

// i18n translation hook
import { useTranslation } from 'react-i18next';

// Types and Interfaces
import {
  ISidebarItem,
  ISubItem,
} from '../../common/interfaces/SidebarItem.interface';

// React Router Link
import { NavLink, useLocation } from 'react-router-dom';

// User Info Context
import { useUserInfo } from '../../auth/UserInfoProvider';
import { isSuperAdmin } from '../../auth/utils/checkUserPermissions';
import { DashboardUserRole } from '../../common/enums/DashboardUserRole';

export const ItemsGroup: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userInfo } = useUserInfo();
  const isSheikhUser = userInfo?.role === DashboardUserRole.Sheikh;
  const isSuperAdminUser = isSuperAdmin(userInfo?.role);

  const sidebarItems: ISidebarItem[] = [
    {
      icon: HiChartPie,
      label: t('sidebar.dashboard'),
      link: '/',
      isUserAllowed: !isSheikhUser,
    },
    {
      icon: HiOutlineDocumentAdd,
      label: t('sidebar.contentManagement'),
      isUserAllowed: !isSheikhUser,
      subItems: [
        {
          label: t('sidebar.contentContext'),
          link: '/content-context',
          isUserAllowed: true,
        },
        {
          label: t('sidebar.content'),
          link: '/content',
          isUserAllowed: true,
        },
        {
          label: t('sidebar.media'),
          link: '/media',
          isUserAllowed: true,
        },
        {
          label: t('sidebar.questions'),
          link: '/questions',
          isUserAllowed: true,
        },
        {
          label: t('sidebar.answers'),
          link: '/answers',
          isUserAllowed: true,
        },
        {
          label: t('sidebar.notificationConfiguration'),
          link: '/notification-configuration',
          isUserAllowed: isSuperAdminUser,
        },
        {
          label: t('sidebar.questionnaire'),
          link: '/questionnaire',
          isUserAllowed: true,
        },
      ],
    },
    {
      icon: HiOutlineCollection,
      label: t('sidebar.versionsManagement'),
      link: '/versions-management',
      isUserAllowed: !isSheikhUser && isSuperAdminUser,
    },
    {
      icon: HiUser,
      label: t('sidebar.usersManagement'),
      isUserAllowed: !isSheikhUser && isSuperAdminUser,
      subItems: [
        {
          label: t('sidebar.users'),
          link: '/user-list',
          isUserAllowed: !isSheikhUser && isSuperAdminUser,
        },
      ],
    },
    {
      icon: HiSpeakerphone,
      label: t('sidebar.broadcast'),
      isUserAllowed: !isSheikhUser && isSuperAdminUser,
      subItems: [
        {
          label: t('sidebar.broadcastList'),
          link: '/broadcast',
          isUserAllowed: !isSheikhUser && isSuperAdminUser,
        },
        {
          label: t('sidebar.broadcastCreate'),
          link: '/broadcast/create',
          isUserAllowed: !isSheikhUser && isSuperAdminUser,
        },
      ],
    },
    {
      icon: HiScale,
      label: t('sidebar.istifta'),
      isUserAllowed: isSheikhUser || isSuperAdminUser,
      subItems: [
        {
          label: t('sidebar.istifta'),
          link: '/istifta',
          isUserAllowed: true,
        },
        {
          label: t('istiftaLinks.sidebar'),
          link: '/istifta/links',
          isUserAllowed: true,
        },
      ],
    },
    {
      icon: BiSupport,
      label: t('sidebar.technicalSupportManagement'),
      link: '/technical-support',
      isUserAllowed: !isSheikhUser,
    },
    {
      icon: HiAcademicCap,
      label: t('sidebar.sessions'),
      link: '/sessions',
      isUserAllowed: !isSheikhUser && isSuperAdminUser,
    },
  ];

  return (
    <FbSidebar.ItemGroup>
      {sidebarItems.map((item) =>
        !item.isUserAllowed ? null : item.subItems &&
          item.subItems.length > 0 ? (
          <FbSidebar.Collapse
            key={item.label}
            className="sidebar__item collapse__item"
            icon={item.icon}
            label={item.label}
            open={item.subItems.some(
              (subItem) => subItem.link === location.pathname
            )}
          >
            <SubItems subItems={item.subItems} />
          </FbSidebar.Collapse>
        ) : (
          <FbSidebar.Item
            key={item.label}
            className={`sidebar__item`}
            icon={item.icon}
            as={NavLink}
            to={item.link || ''}
            end
          >
            {item.label}
          </FbSidebar.Item>
        )
      )}
    </FbSidebar.ItemGroup>
  );
};

const SubItems: React.FC<{ subItems: ISubItem[] }> = ({ subItems }) => {
  return subItems ? (
    <>
      {subItems.map((subItem) =>
        !subItem.isUserAllowed ? null : (
          <FbSidebar.Item
            key={subItem.label}
            className={`sidebar__item`}
            as={NavLink}
            to={subItem.link || ''}
            end
          >
            {subItem.label}
          </FbSidebar.Item>
        )
      )}
    </>
  ) : null;
};
